// Shared format/label helpers — German labels for status + priority enums.

import type {
  PhaseStatus,
  ProjectStatus,
  TaskPriority,
  TaskStatus,
} from "@juuplan/shared";

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  active: "Aktiv",
  paused: "Pausiert",
  done: "Abgeschlossen",
  archived: "Archiviert",
};

export const PHASE_STATUS_LABEL: Record<PhaseStatus, string> = {
  planned: "Geplant",
  active: "Aktiv",
  done: "Abgeschlossen",
};

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog",
  in_progress: "In Arbeit",
  review: "Review",
  done: "Erledigt",
  blocked: "Blockiert",
};

export const TASK_PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: "Niedrig",
  medium: "Mittel",
  high: "Hoch",
  urgent: "Dringend",
};

export const TASK_STATUS_VAR: Record<TaskStatus, string> = {
  backlog: "var(--color-status-backlog)",
  in_progress: "var(--color-status-in-progress)",
  review: "var(--color-status-review)",
  done: "var(--color-status-done)",
  blocked: "var(--color-status-blocked)",
};

export const TASK_PRIORITY_VAR: Record<TaskPriority, string> = {
  low: "var(--color-prio-low)",
  medium: "var(--color-prio-medium)",
  high: "var(--color-prio-high)",
  urgent: "var(--color-prio-urgent)",
};

export const PROJECT_STATUS_VAR: Record<ProjectStatus, string> = {
  active: "var(--color-status-in-progress)",
  paused: "var(--color-status-review)",
  done: "var(--color-status-done)",
  archived: "var(--color-outline)",
};

/**
 * Soft-tinted pill style for status/priority chips (Clean & Focused look):
 * a low-opacity tint of the accent color as background + the accent color as
 * text, nudged towards the theme's on-surface color so light hues (yellow,
 * light green) stay readable on the tint — in light AND dark theme.
 * Accepts either a CSS var string ("var(--color-…)") or a hex.
 */
export function softPillStyle(color: string): { background: string; color: string } {
  return {
    background: `color-mix(in srgb, ${color} 14%, transparent)`,
    // 45 % Farbe + 55 % Textfarbe: haelt >= 4,5:1 auch fuer helle, frei
    // gewaehlte Statusfarben (z. B. #EAB308, #10B981) auf ihrer 14-%-Toenung,
    // in hell wie dunkel. Bei 78 % lagen solche Pillen bei 2,6–3,2:1.
    color: `color-mix(in srgb, ${color} 45%, var(--color-on-surface))`,
  };
}

/** Format a Date as local-timezone YYYY-MM-DD (no UTC shift). */
export function localDateIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Today's date in the USER'S timezone as YYYY-MM-DD.
 * `new Date().toISOString().slice(0, 10)` is the UTC date — for CET/CEST users
 * that lags the wall-clock date until 1/2 a.m., so overdue markers, due
 * bucketing and "Mein Tag" pointed at yesterday. Always use this instead.
 */
export function localTodayIso(): string {
  return localDateIso(new Date());
}

/** Parse a "YYYY-MM-DD" string as a local-time Date (no UTC off-by-one). */
function parseLocalIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Add `delta` calendar days (local time) to an ISO date. Used by the #876
 * drag & drop date math in CalendarView/TaskCalendarView/MilestonesView —
 * kept here (not per-view) so all three shift dates identically.
 */
export function addDaysIso(iso: string, delta: number): string {
  const d = parseLocalIsoDate(iso);
  if (!d) return iso;
  const next = new Date(d);
  next.setDate(next.getDate() + delta);
  return localDateIso(next);
}

/** Whole calendar days between two ISO dates (toIso - fromIso). */
export function diffDaysIso(fromIso: string, toIso: string): number {
  const a = parseLocalIsoDate(fromIso);
  const b = parseLocalIsoDate(toIso);
  if (!a || !b) return 0;
  const MS = 24 * 60 * 60 * 1000;
  return Math.round((b.getTime() - a.getTime()) / MS);
}

export function formatDateShort(iso: string | null): string {
  if (!iso) return "—";
  // ISO date "YYYY-MM-DD" or full timestamp — both safe to parse.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export function formatDateTimeShort(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function initials(name: string): string {
  // Erstes + letztes Wort (nicht die ersten zwei): „Maximilian von
  // Hohenstein-Weißenfels" ist „MH", nicht „MV". Zeichen per Array.from, damit
  // ein Emoji am Wortanfang nicht als halbes Surrogat („�") erscheint.
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const pick = parts.length === 1 ? [parts[0]] : [parts[0], parts[parts.length - 1]];
  return pick.map((p) => (Array.from(p)[0] ?? "").toUpperCase()).join("") || "?";
}

/** Preset palette for project + phase color picker. */
export const COLOR_PRESETS = [
  "#2563EB", // primary
  "#006C49", // secondary
  "#00569C", // tertiary
  "#A16207", // warning
  "#BA1A1A", // error
  "#7C3AED", // violet
  "#DB2777", // pink
  "#EA580C", // orange
  "#0891B2", // cyan
  "#737686", // outline (neutral)
] as const;
